import { useState, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { Button, Modal, TextField, Text, Badge, Popover, ActionList, Select } from '@shopify/polaris';
import { postRequest, getRequest, deleteRequest } from '../../../../shared/components/functions.jsx';
import { formatToCurrency } from '../../../../shared/components/functions.jsx';
import { STATUS_LABELS, STATUS_BADGE_TONES } from '../../../../config/roasterySpec';

// Custom option for cross-project quotation search
const QuotationOption = ({ innerProps, data, isFocused, isSelected }) => (
  <div
    {...innerProps}
    style={{
      padding: '8px 12px',
      backgroundColor: isSelected ? '#2684FF' : isFocused ? '#DEEBFF' : 'white',
      color: isSelected ? 'white' : 'inherit',
      cursor: 'pointer',
      borderBottom: '1px solid #eee'
    }}
  >
    <div style={{ fontWeight: 500 }}>
      {data.projectNumber} — v{data.versionNumber}
      {data.versionLabel ? ` (${data.versionLabel})` : ''}
    </div>
    <div style={{ fontSize: '12px', color: isSelected ? 'rgba(255,255,255,0.8)' : '#666', display: 'flex', gap: '8px' }}>
      <span>{data.projectDesc}</span>
      {data.clientName && <span>· {data.clientName}</span>}
      <span>· {data.lineCount} lines</span>
      {data.totalSelling > 0 && <span>· {formatToCurrency(data.totalSelling)}</span>}
    </div>
  </div>
);

function getVersionDisplayName(quotation) {
  const custom = String(quotation?.VersionLabel || '').trim();
  return custom || `v${quotation?.VersionNumber || ''}`;
}

function getVersionSubLabel(quotation) {
  const custom = String(quotation?.VersionLabel || '').trim();
  if (!custom) return null;
  return `v${quotation?.VersionNumber || ''}`;
}

export default function VersionManager({ projectId, quotations, selectedId, onSelect, onRefresh, onCompare, onConvertToSale, onOpenCustomerInteractions }) {
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [modal, setModal] = useState(null); // 'new' | 'rename' | 'status'
  const [versionLabel, setVersionLabel] = useState('');
  const [newMode, setNewMode] = useState('empty'); // 'empty' | 'branch'
  const [branchFromId, setBranchFromId] = useState(null);
  const [branchSource, setBranchSource] = useState('local'); // 'local' | 'other'
  const [externalSelection, setExternalSelection] = useState(null);
  const [loading, setLoading] = useState(false);

  // Share modal state
  const [shareRecipientName, setShareRecipientName] = useState('');
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [shareExpiryDays, setShareExpiryDays] = useState('30');
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLinksLoading, setShareLinksLoading] = useState(false);
  const [shareVersionId, setShareVersionId] = useState('');
  const [generatedLink, setGeneratedLink] = useState(null);

  // Acceptance data cache per quotation
  const [acceptanceCache, setAcceptanceCache] = useState({});

  const selected = quotations.find((q) => q.ID === selectedId);
  const shareableQuotations = quotations.filter((q) => q.Status === 1);
  const shareAnchorQuotationId = selectedId || quotations[0]?.ID || null;
  const activeShareLinkCount = shareLinks.filter((link) => link.computedStatus === 'active').length;

  // Fetch acceptance data for all accepted quotations
  useEffect(() => {
    for (const q of quotations) {
      if (q.Status === 2 && !acceptanceCache[q.ID]) {
        getRequest(`/api/project-quotations/${q.ID}/acceptance`).then((data) => {
          if (data) setAcceptanceCache((prev) => ({ ...prev, [q.ID]: data }));
        });
      }
    }
  }, [quotations]);

  const loadShareLinks = async (anchorQuotationId = shareAnchorQuotationId) => {
    if (!anchorQuotationId) {
      setShareLinks([]);
      return [];
    }
    setShareLinksLoading(true);
    const links = await getRequest(`/api/project-quotations/${anchorQuotationId}/shares`);
    const normalized = links || [];
    setShareLinks(normalized);
    setShareLinksLoading(false);
    return normalized;
  };

  useEffect(() => {
    loadShareLinks();
  }, [shareAnchorQuotationId, quotations.length]);

  const closeModal = () => {
    setModal(null);
    setVersionLabel('');
    setNewMode('empty');
    setBranchFromId(null);
    setBranchSource('local');
    setExternalSelection(null);
    setShareRecipientName('');
    setShareRecipientEmail('');
    setShareExpiryDays('30');
    setShareVersionId('');
    setGeneratedLink(null);
  };

  const openNewModal = (preselectedBranchId) => {
    setVersionLabel('');
    setExternalSelection(null);
    if (preselectedBranchId && quotations.length > 0) {
      setNewMode('branch');
      setBranchSource('local');
      setBranchFromId(String(preselectedBranchId));
    } else if (quotations.length > 0) {
      setNewMode('branch');
      setBranchSource('local');
      setBranchFromId(String(quotations[quotations.length - 1].ID));
    } else {
      setNewMode('empty');
      setBranchSource('other');
      setBranchFromId(null);
    }
    setModal('new');
  };

  const loadExternalQuotations = async (inputValue, callback) => {
    if (!inputValue || inputValue.trim().length < 2) {
      callback([]);
      return;
    }
    const data = await getRequest(`/api/project-quotations/search?q=${encodeURIComponent(inputValue.trim())}`);
    callback(
      (data || []).map((r) => ({
        value: r.ID,
        label: `${r.Projectnummer} v${r.VersionNumber}`,
        projectNumber: r.Projectnummer,
        projectDesc: r.ProjectDescription || '',
        clientName: r.ClientName || '',
        versionNumber: r.VersionNumber,
        versionLabel: r.VersionLabel || '',
        lineCount: r.LineCount || 0,
        totalSelling: r.TotalSelling || 0
      }))
    );
  };

  const effectiveBranchId = newMode === 'branch' ? (branchSource === 'local' ? branchFromId : externalSelection?.value ? String(externalSelection.value) : null) : null;

  const handleCreate = async () => {
    setLoading(true);
    let result;
    if (newMode === 'branch' && effectiveBranchId) {
      result = await postRequest(`/api/project-quotations/${effectiveBranchId}/copy`, {
        TargetProjectID: projectId,
        VersionLabel: versionLabel || null
      });
    } else {
      result = await postRequest('/api/project-quotations', {
        ProjectID: projectId,
        VersionLabel: versionLabel || null
      });
    }
    setLoading(false);
    closeModal();
    if (result?.ID) onRefresh(result.ID);
  };

  const handleRename = async () => {
    if (!selectedId) return;
    setLoading(true);
    await fetch(`/api/project-quotations/${selectedId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ VersionLabel: versionLabel || null })
    });
    setLoading(false);
    closeModal();
    onRefresh(selectedId);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedId) return;
    setLoading(true);
    await fetch(`/api/project-quotations/${selectedId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ Status: newStatus })
    });
    setLoading(false);
    closeModal();
    onRefresh(selectedId);
  };

  const handleDelete = async () => {
    if (!selectedId || !selected || selected.Status !== 0) return;
    if (!window.confirm('Delete this draft quotation? This cannot be undone.')) return;
    setLoading(true);
    await fetch(`/api/project-quotations/${selectedId}`, { method: 'DELETE' });
    setLoading(false);
    onRefresh(null);
  };

  const openShareModal = async () => {
    setModal('share');
    setGeneratedLink(null);
    if (selected?.Status === 1) {
      setShareVersionId(String(selected.ID));
    } else {
      const latestShareable = shareableQuotations[shareableQuotations.length - 1];
      setShareVersionId(latestShareable ? String(latestShareable.ID) : '');
    }
    await loadShareLinks();
  };

  const handleGenerateShareLink = async () => {
    if (!shareVersionId) {
      alert('Select a Ready quotation version first.');
      return;
    }
    setLoading(true);
    const result = await postRequest(`/api/project-quotations/${shareVersionId}/share`, {
      recipientEmail: shareRecipientEmail || null,
      recipientName: shareRecipientName || null,
      expiresInDays: parseInt(shareExpiryDays) || 30,
      sendEmail: false
    });
    setLoading(false);
    if (result?.Token) {
      const url = `${window.location.origin}/quote/${result.Token}`;
      setGeneratedLink(url);
      navigator.clipboard?.writeText(url);
      await loadShareLinks();
    }
  };

  const handleRevokeLink = async (linkId) => {
    await deleteRequest(`/api/project-quotations/shares/${linkId}`);
    await loadShareLinks();
  };

  const openMenu = (e, qId) => {
    e.stopPropagation();
    setMenuOpenId(menuOpenId === qId ? null : qId);
  };

  const menuActions = (q) => {
    const idx = quotations.findIndex((v) => v.ID === q.ID);
    const prevQ = idx > 0 ? quotations[idx - 1] : null;

    // Accepted quotations: only allow branching, comparing, and converting to sale
    if (q.Status === 2) {
      const actions = [
        {
          content: 'Rename',
          onAction: () => {
            setMenuOpenId(null);
            onSelect(q.ID);
            setVersionLabel(q.VersionLabel || '');
            setModal('rename');
          }
        },
        {
          content: 'Convert to Sale',
          onAction: () => {
            setMenuOpenId(null);
            onSelect(q.ID);
            if (onConvertToSale) onConvertToSale(q.ID);
          }
        },
        {
          content: 'Branch new version',
          onAction: () => {
            setMenuOpenId(null);
            openNewModal(q.ID);
          }
        }
      ];
      if (onCompare) {
        actions.push({
          content: 'Compare with previous',
          disabled: !prevQ,
          onAction: () => {
            setMenuOpenId(null);
            onCompare(prevQ.ID, q.ID);
          }
        });
      }
      return actions;
    }

    const actions = [
      {
        content: 'Rename',
        onAction: () => {
          setMenuOpenId(null);
          onSelect(q.ID);
          setVersionLabel(q.VersionLabel || '');
          setModal('rename');
        }
      },
      {
        content: 'Branch new version',
        onAction: () => {
          setMenuOpenId(null);
          openNewModal(q.ID);
        }
      },
      {
        content: 'Change status',
        onAction: () => {
          setMenuOpenId(null);
          onSelect(q.ID);
          setModal('status');
        }
      }
    ];
    if (onCompare) {
      actions.push({
        content: 'Compare with previous',
        disabled: !prevQ,
        onAction: () => {
          setMenuOpenId(null);
          onCompare(prevQ.ID, q.ID);
        }
      });
    }
    if (q.Status === 0) {
      actions.push({
        content: 'Delete',
        destructive: true,
        onAction: () => {
          setMenuOpenId(null);
          onSelect(q.ID);
          setTimeout(handleDelete, 50);
        }
      });
    }
    return actions;
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', borderBottom: '1px solid #e1e3e5', paddingBottom: '0' }}>
        {quotations.map((q, idx) => {
          const isActive = q.ID === selectedId;
          const total = q.totals?.combined?.totalSelling || 0;
          const prevTotal = idx > 0 ? quotations[idx - 1].totals?.combined?.totalSelling || 0 : null;
          const delta = prevTotal != null && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;
          return (
            <div
              key={q.ID}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: isActive ? '2px solid #000' : '2px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: isActive ? '#f6f6f7' : 'transparent',
                borderRadius: '6px 6px 0 0',
                transition: 'background-color 0.15s ease',
                position: 'relative'
              }}
              onClick={() => onSelect(q.ID)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <Text variant="bodySm" as="span" fontWeight={isActive ? 'semibold' : 'regular'}>
                  {getVersionDisplayName(q)}
                </Text>
                {getVersionSubLabel(q) && (
                  <Text variant="bodySm" as="span" tone="subdued">
                    {getVersionSubLabel(q)}
                  </Text>
                )}
              </div>
              <Badge tone={STATUS_BADGE_TONES[q.Status]} size="small">
                {STATUS_LABELS[q.Status]}
              </Badge>
              {q.Status === 2 && acceptanceCache[q.ID] && (
                <span title={`Accepted by ${acceptanceCache[q.ID].SignatoryName}`} style={{ fontSize: '11px', color: '#2e7d32' }}>
                  &#10003;
                </span>
              )}
              {total > 0 && (
                <Text variant="bodySm" as="span" tone="subdued">
                  {formatToCurrency(total)}
                </Text>
              )}
              {delta != null && total > 0 && (
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '10px',
                    backgroundColor: delta > 0 ? '#e8f5e9' : delta < 0 ? '#fce4ec' : '#f5f5f5',
                    color: delta > 0 ? '#2e7d32' : delta < 0 ? '#c62828' : '#666'
                  }}
                >
                  {delta > 0 ? '+' : ''}
                  {delta.toFixed(1)}%
                </span>
              )}
              <Popover
                active={menuOpenId === q.ID}
                activator={
                  <span onClick={(e) => openMenu(e, q.ID)} style={{ cursor: 'pointer', fontSize: '14px', color: '#888', padding: '0 2px', lineHeight: 1 }}>
                    &#8943;
                  </span>
                }
                onClose={() => setMenuOpenId(null)}
                preferredAlignment="left"
              >
                <ActionList items={menuActions(q)} />
              </Popover>
            </div>
          );
        })}

        {/* + tab to create new version */}
        <div
          onClick={() => openNewModal(null)}
          style={{
            padding: '8px 14px',
            cursor: 'pointer',
            borderBottom: '2px solid transparent',
            marginBottom: '-1px',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '6px 6px 0 0',
            color: '#888',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          +
        </div>

        <div style={{ marginLeft: 'auto', padding: '6px 0 6px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={openShareModal}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '7px 12px',
              borderRadius: '999px',
              border: '1px solid #d8e6d2',
              background: 'linear-gradient(135deg, #f1f8ee 0%, #f8fcf6 100%)',
              color: '#2d6a22',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.02em',
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(24, 94, 16, 0.12)',
              transition: 'box-shadow 0.12s ease, border-color 0.12s ease, background-color 0.12s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(24, 94, 16, 0.14)';
              e.currentTarget.style.borderColor = '#c8dcbf';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 2px rgba(24, 94, 16, 0.12)';
              e.currentTarget.style.borderColor = '#d8e6d2';
            }}
          >
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#5da551', boxShadow: '0 0 0 3px rgba(93, 165, 81, 0.16)' }} />
            Shared Links
            {activeShareLinkCount > 0 && (
              <span style={{ fontSize: '11px', fontWeight: 700, color: '#1f4d16', padding: '1px 6px', borderRadius: '999px', background: '#e6f4df' }}>{activeShareLinkCount}</span>
            )}
          </button>

          {onOpenCustomerInteractions && (
            <button
              type="button"
              onClick={onOpenCustomerInteractions}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '7px 12px',
                borderRadius: '999px',
                border: '1px solid #d4def8',
                background: 'linear-gradient(135deg, #edf3ff 0%, #f7f9ff 100%)',
                color: '#1f3f9a',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(19, 49, 142, 0.12)',
                transition: 'box-shadow 0.12s ease, border-color 0.12s ease, background-color 0.12s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(19, 49, 142, 0.14)';
                e.currentTarget.style.borderColor = '#bdcdf6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(19, 49, 142, 0.12)';
                e.currentTarget.style.borderColor = '#d4def8';
              }}
            >
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#4f7cff', boxShadow: '0 0 0 3px rgba(79, 124, 255, 0.15)' }} />
              Customer Interactions
            </button>
          )}
        </div>
      </div>

      {/* New Version Modal */}
      <Modal
        open={modal === 'new'}
        onClose={closeModal}
        title="Create New Quotation Version"
        primaryAction={{ content: newMode === 'branch' ? 'Branch' : 'Create', onAction: handleCreate, loading, disabled: newMode === 'branch' && !effectiveBranchId }}
        secondaryActions={[{ content: 'Cancel', onAction: closeModal }]}
      >
        <Modal.Section>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading && !(newMode === 'branch' && !effectiveBranchId)) handleCreate();
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <Text variant="bodySm" as="p" fontWeight="semibold">
                  Starting point
                </Text>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setNewMode('branch');
                      if (quotations.length > 0) {
                        setBranchSource('local');
                        setBranchFromId(String(quotations[quotations.length - 1].ID));
                      } else {
                        setBranchSource('other');
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: newMode === 'branch' ? '2px solid #000' : '1px solid #ccc',
                      backgroundColor: newMode === 'branch' ? '#f6f6f7' : '#fff',
                      textAlign: 'left'
                    }}
                  >
                    <Text variant="bodySm" as="span" fontWeight="semibold">
                      Branch from existing
                    </Text>
                    <br />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Copy all lines & config from a version
                    </Text>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNewMode('empty');
                      setBranchFromId(null);
                      setExternalSelection(null);
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: newMode === 'empty' ? '2px solid #000' : '1px solid #ccc',
                      backgroundColor: newMode === 'empty' ? '#f6f6f7' : '#fff',
                      textAlign: 'left'
                    }}
                  >
                    <Text variant="bodySm" as="span" fontWeight="semibold">
                      Start empty
                    </Text>
                    <br />
                    <Text variant="bodySm" as="span" tone="subdued">
                      Begin with a blank quotation
                    </Text>
                  </button>
                </div>
              </div>

              {newMode === 'branch' && (
                <>
                  {quotations.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setBranchSource('local');
                          setBranchFromId(String(quotations[quotations.length - 1].ID));
                          setExternalSelection(null);
                        }}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          border: '1px solid #ccc',
                          backgroundColor: branchSource === 'local' ? '#333' : '#fff',
                          color: branchSource === 'local' ? '#fff' : '#333',
                          fontWeight: branchSource === 'local' ? 600 : 400
                        }}
                      >
                        This project
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setBranchSource('other');
                          setBranchFromId(null);
                        }}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          border: '1px solid #ccc',
                          backgroundColor: branchSource === 'other' ? '#333' : '#fff',
                          color: branchSource === 'other' ? '#fff' : '#333',
                          fontWeight: branchSource === 'other' ? 600 : 400
                        }}
                      >
                        Other project
                      </button>
                    </div>
                  )}

                  {branchSource === 'local' && quotations.length > 0 && (
                    <Select
                      label="Branch from"
                      options={quotations.map((q) => ({
                        label: `${getVersionDisplayName(q)}${getVersionSubLabel(q) ? ` (${getVersionSubLabel(q)})` : ''} (${formatToCurrency(q.totals?.combined?.totalSelling || 0)})`,
                        value: String(q.ID)
                      }))}
                      value={branchFromId || ''}
                      onChange={setBranchFromId}
                    />
                  )}

                  {(branchSource === 'other' || quotations.length === 0) && (
                    <div>
                      <Text variant="bodySm" as="p" fontWeight="regular">
                        Search by project number, description, or client
                      </Text>
                      <div style={{ marginTop: '4px' }}>
                        <AsyncSelect
                          value={externalSelection}
                          loadOptions={loadExternalQuotations}
                          menuPortalTarget={document.body}
                          placeholder="Type to search quotations..."
                          isClearable
                          components={{ Option: QuotationOption }}
                          styles={{
                            control: (base) => ({ ...base, minHeight: '36px', borderRadius: '8px', borderColor: '#919191' }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            menu: (base) => ({ ...base, zIndex: 9999, width: 'max(100%, 450px)' })
                          }}
                          onChange={(opt) => {
                            setExternalSelection(opt);
                          }}
                          noOptionsMessage={({ inputValue }) => (inputValue?.length < 2 ? 'Type at least 2 characters...' : 'No quotations found')}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              <TextField label="Version label (optional)" value={versionLabel} onChange={setVersionLabel} placeholder="e.g. Revised pricing, Budget option" autoComplete="off" />
            </div>
          </form>
        </Modal.Section>
      </Modal>

      {/* Rename Modal */}
      <Modal
        open={modal === 'rename'}
        onClose={closeModal}
        title={`Rename ${selected ? getVersionDisplayName(selected) : ''}`}
        primaryAction={{ content: 'Save', onAction: handleRename, loading }}
        secondaryActions={[{ content: 'Cancel', onAction: closeModal }]}
      >
        <Modal.Section>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!loading) handleRename();
            }}
          >
            <TextField label="Version label" value={versionLabel} onChange={setVersionLabel} placeholder="e.g. Initial, Revised pricing" autoComplete="off" />
          </form>
        </Modal.Section>
      </Modal>

      {/* Status Change Modal */}
      <Modal open={modal === 'status'} onClose={closeModal} title="Change Quotation Status">
        <Modal.Section>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {[0, 1, 2, 3, 4].map((s) => (
              <Button key={s} onClick={() => handleStatusChange(s)} disabled={selected?.Status === s} size="slim">
                {STATUS_LABELS[s]}
              </Button>
            ))}
          </div>
        </Modal.Section>
      </Modal>

      {/* Share for Acceptance Modal */}
      <Modal open={modal === 'share'} onClose={closeModal} title="Shared Links" large>
        <Modal.Section>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Text variant="bodySm" as="p" tone="subdued">
              Generate and manage customer share links for this project.
            </Text>

            {shareableQuotations.length > 0 ? (
              <div style={{ width: '280px' }}>
                <Select
                  label="Version to share"
                  options={shareableQuotations.map((q) => ({
                    label: `${getVersionDisplayName(q)}${getVersionSubLabel(q) ? ` (${getVersionSubLabel(q)})` : ''}`,
                    value: String(q.ID)
                  }))}
                  value={shareVersionId}
                  onChange={setShareVersionId}
                />
              </div>
            ) : (
              <Text variant="bodySm" as="p" tone="critical">
                No Ready quotation version available to share. Set a version status to Ready first.
              </Text>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <TextField label="Recipient name" value={shareRecipientName} onChange={setShareRecipientName} placeholder="e.g. John Smith" autoComplete="off" />
              </div>
              <div style={{ flex: 1 }}>
                <TextField label="Recipient email" value={shareRecipientEmail} onChange={setShareRecipientEmail} placeholder="e.g. john@company.com" type="email" autoComplete="off" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ width: '180px' }}>
                <Select
                  label="Link validity"
                  options={[
                    { label: '7 days', value: '7' },
                    { label: '14 days', value: '14' },
                    { label: '30 days', value: '30' },
                    { label: '60 days', value: '60' }
                  ]}
                  value={shareExpiryDays}
                  onChange={setShareExpiryDays}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button primary onClick={handleGenerateShareLink} loading={loading} disabled={!shareVersionId}>
                Generate Link
              </Button>
            </div>

            {generatedLink && (
              <div style={{ padding: '12px 16px', background: '#e8f5e9', borderRadius: '8px' }}>
                <Text variant="bodySm" as="p" fontWeight="semibold">
                  Link generated and copied to clipboard!
                </Text>
                <div style={{ marginTop: '4px', fontSize: '12px', wordBreak: 'break-all', color: '#333', fontFamily: 'monospace' }}>{generatedLink}</div>
                <div style={{ marginTop: '8px' }}>
                  <Button
                    size="slim"
                    onClick={() => {
                      window.open(generatedLink, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    Open
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Modal.Section>

        {/* Existing share links */}
        <Modal.Section>
          <Text variant="headingSm" as="h3">
            Existing Share Links
          </Text>
          {shareLinksLoading ? (
            <Text variant="bodySm" as="p" tone="subdued">
              Loading...
            </Text>
          ) : shareLinks.length === 0 ? (
            <Text variant="bodySm" as="p" tone="subdued">
              No share links created yet.
            </Text>
          ) : (
            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {shareLinks.map((link) => (
                <div key={link.ID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f6f6f7', borderRadius: '8px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {link.RecipientName && (
                        <Text variant="bodySm" as="span" fontWeight="semibold">
                          {link.RecipientName}
                        </Text>
                      )}
                      {link.RecipientEmail && (
                        <Text variant="bodySm" as="span" tone="subdued">
                          {link.RecipientEmail}
                        </Text>
                      )}
                      <Badge tone={link.computedStatus === 'active' ? 'success' : link.computedStatus === 'expired' ? 'warning' : 'critical'} size="small">
                        {link.computedStatus}
                      </Badge>
                    </div>
                    <Text variant="bodySm" as="p" tone="subdued">
                      Created {new Date(link.CreatedAt).toLocaleDateString()} · Expires {new Date(link.ExpiresAt).toLocaleDateString()}
                      {link.LastAccessedAt ? ` · Last accessed ${new Date(link.LastAccessedAt).toLocaleDateString()}` : ''}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      size="slim"
                      onClick={() => {
                        const url = `${window.location.origin}/quote/${link.Token}`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      size="slim"
                      onClick={() => {
                        const url = `${window.location.origin}/quote/${link.Token}`;
                        navigator.clipboard?.writeText(url);
                      }}
                    >
                      Copy
                    </Button>
                    {link.computedStatus === 'active' && (
                      <Button size="slim" destructive onClick={() => handleRevokeLink(link.ID)}>
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Section>
      </Modal>
    </>
  );
}
